import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { MagnifyingGlassIcon, PhotoIcon, RectangleGroupIcon, DocumentTextIcon, XMarkIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const formatPrezzo = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function BikeCatalog() {
  const [bici, setBici] = useState([]);
  const [marche, setMarche] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [fMarca, setFMarca] = useState('');
  const [sel, setSel] = useState(null); // bici selezionata per la modale dettaglio
  const [pdfUrl, setPdfUrl] = useState(null); // scheda tecnica aperta nel visualizzatore modale

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/bici/marche');
        setMarche(data || []);
      } catch (e) { /* ignora */ }
    })();
  }, []);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 24, sort: 'brand', order: 'ASC' };
      if (search) params.search = search;
      if (fMarca) params.marca_id = fMarca;
      const { data } = await api.get('/bici', { params });
      setBici(data.bici || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 0);
    } catch (e) {
      toast.error('Errore caricamento catalogo');
    } finally {
      setLoading(false);
    }
  }, [page, search, fMarca]);

  useEffect(() => { carica(); }, [carica]);

  return (
    <div className="relative min-h-[calc(100vh-4rem)] bg-gradient-to-br from-gray-50 via-white to-emerald-50/40">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(16,185,129,0.06)_1px,transparent_0)] [background-size:24px_24px] pointer-events-none" />

      <div className="relative z-10 max-w-[1800px] mx-auto px-4 md:px-6 py-6">
        {/* Header */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-5 border border-white/40 shadow-xl shadow-black/5 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <RectangleGroupIcon className="h-6 w-6 text-emerald-600" /> Catalogo Bici
              </h1>
              <p className="text-sm text-gray-500">{total} prodotti disponibili</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <div className="relative flex-1 lg:w-72">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-2.5" />
                <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                  placeholder="Cerca modello, brand, nome…"
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm bg-white/80" />
              </div>
              <select value={fMarca} onChange={(e) => { setPage(1); setFMarca(e.target.value); }}
                className="border border-gray-200 rounded-xl text-sm px-3 py-2 bg-white/80">
                <option value="">Tutti i brand</option>
                {marche.map((m) => <option key={m.id} value={m.id}>{m.descrizione}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Griglia prodotti */}
        {loading ? (
          <div className="text-center text-gray-400 py-20">Caricamento catalogo…</div>
        ) : bici.length === 0 ? (
          <div className="text-center text-gray-400 py-20">
            <RectangleGroupIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            Nessun prodotto trovato.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {bici.map((b) => (
              <div key={b.id} onClick={() => setSel(b)}
                className="bg-white rounded-2xl border border-gray-200/70 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all flex flex-col cursor-pointer">
                <div className="aspect-[4/3] bg-gradient-to-br from-gray-50 to-gray-100/50 flex items-center justify-center overflow-hidden">
                  {b.immagine?.path_file
                    ? <img src={b.immagine.path_file} alt={b.modello || ''} className="w-full h-full object-contain p-3" />
                    : <PhotoIcon className="h-10 w-10 text-gray-300" />}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wide truncate">
                      {b.marca?.descrizione || b.brand || '—'}
                    </span>
                    {b.arrivoPrevisto && (
                      <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                        {b.arrivoPrevisto}
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-gray-900 mt-1 leading-tight">{b.modello || '—'}</h3>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 flex-1">{b.nomeProdotto || ''}</p>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-lg font-bold text-gray-900 leading-none">{formatPrezzo(b.prezzoPubblico)}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">IVA inclusa</p>
                    {b.schedaTecnicaPdf && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setPdfUrl(b.schedaTecnicaPdf); }}
                        className="mt-2.5 inline-flex items-center justify-center gap-1.5 w-full text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-100 rounded-lg py-2 transition-colors">
                        <DocumentTextIcon className="h-4 w-4" /> Scheda tecnica
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Paginazione */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6 text-sm">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg bg-white disabled:opacity-40">Precedente</button>
            <span className="text-gray-600">Pagina {page} di {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg bg-white disabled:opacity-40">Successiva</button>
          </div>
        )}
      </div>

      {sel && <DettaglioModal bici={sel} onClose={() => setSel(null)} onOpenPdf={(u) => setPdfUrl(u)} />}
      {pdfUrl && <PdfModal url={pdfUrl} onClose={() => setPdfUrl(null)} />}
    </div>
  );
}

// Visualizzatore PDF in modale (in-app, niente nuova scheda)
function PdfModal({ url, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-gray-900/70 backdrop-blur-sm p-0 sm:p-6" role="dialog" aria-modal="true">
      <div className="relative bg-white rounded-none sm:rounded-2xl shadow-2xl w-full h-full sm:max-w-5xl sm:mx-auto flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 bg-white">
          <span className="inline-flex items-center gap-2 font-semibold text-gray-900">
            <DocumentTextIcon className="h-5 w-5 text-emerald-600" /> Scheda tecnica
          </span>
          <div className="flex items-center gap-2">
            <a href={url} download className="text-sm text-gray-500 hover:text-emerald-700 px-2 py-1">Scarica</a>
            <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"><XMarkIcon className="h-5 w-5" /></button>
          </div>
        </div>
        <iframe src={url} title="Scheda tecnica" className="flex-1 w-full bg-gray-100" />
      </div>
    </div>
  );
}

// Riga specifica (mostrata solo se valorizzata)
const Spec = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900 text-right">{value}</span>
    </div>
  );
};

// Modale dettaglio prodotto — stile "Configura il noleggio" (NLT), tema emerald.
function DettaglioModal({ bici: b, onClose, onOpenPdf }) {
  const [showProvvigioni, setShowProvvigioni] = useState(false);
  const pi = b.provvigioneInfo;
  const adminView = pi && pi.percentuale !== undefined; // il backend include la % solo per gli admin
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex items-end sm:items-center justify-center min-h-screen p-0 sm:p-4">
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" />
        <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-100">{b.marca?.descrizione || b.brand || 'Bici'}</p>
              <h3 className="text-xl font-bold leading-tight truncate">{b.modello || '—'}{b.allestimento ? ` · ${b.allestimento}` : ''}</h3>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/15 transition-colors flex-shrink-0"><XMarkIcon className="h-5 w-5" /></button>
          </div>

          {/* Corpo */}
          <div className="p-6 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Immagine */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100/60 rounded-xl border border-gray-100 flex items-center justify-center min-h-[220px] overflow-hidden">
                {b.immagine?.path_file
                  ? <img src={b.immagine.path_file} alt={b.modello || ''} className="w-full h-full object-contain p-4" />
                  : <PhotoIcon className="h-16 w-16 text-gray-300" />}
              </div>

              {/* Caratteristiche */}
              <div>
                {b.nomeProdotto && <p className="text-sm text-gray-600 mb-3">{b.nomeProdotto}</p>}
                <div className="text-sm">
                  <Spec label="Categoria" value={b.categoria?.descrizione} />
                  <Spec label="Tipo" value={b.tipo?.descrizione || (b.elettrica ? 'Elettrica' : 'Muscolare')} />
                  <Spec label="Taglia" value={b.taglia} />
                  <Spec label="EAN" value={b.ean} />
                  {b.elettrica && <>
                    <Spec label="Motore" value={b.motoreMarca} />
                    <Spec label="Potenza" value={b.potenzaMotore ? `${b.potenzaMotore} W` : null} />
                    <Spec label="Batteria" value={b.capacitaBatteria ? `${b.capacitaBatteria} Wh` : null} />
                    <Spec label="Autonomia" value={b.autonomiaKm ? `${b.autonomiaKm} km` : null} />
                  </>}
                  <Spec label="Velocità" value={b.numeroVelocita} />
                  <Spec label="Freni" value={b.freni} />
                  <Spec label="Telaio" value={b.materialeTelaio} />
                  <Spec label="Peso" value={b.peso ? `${b.peso} kg` : null} />
                  <Spec label="Arrivo previsto" value={b.arrivoPrevisto} />
                </div>

                {/* Prezzo */}
                <div className="mt-4 bg-emerald-50/60 border border-emerald-100 rounded-xl px-4 py-3 flex items-end justify-between">
                  <span className="text-sm text-gray-600">Prezzo al pubblico</span>
                  <span className="text-right"><span className="block text-2xl font-bold text-gray-900 leading-none">{formatPrezzo(b.prezzoPubblico)}</span><span className="text-[11px] text-gray-400">IVA inclusa</span></span>
                </div>
              </div>
            </div>

            {/* Provvigioni — nascoste di default, si rivelano col pulsantino discreto (come "Configura noleggio") */}
            {pi ? (
              <div className="mt-6">
                <div className="flex justify-end">
                  <button type="button" onClick={() => setShowProvvigioni((v) => !v)}
                    className="text-xs text-gray-400 hover:text-gray-600 opacity-50 hover:opacity-100 transition-all duration-200"
                    title="Visualizza provvigioni">
                    {showProvvigioni ? '👁️ Nascondi provvigioni' : '💰'}
                  </button>
                </div>
                {showProvvigioni && <div className="mt-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1.5"><BanknotesIcon className="h-4 w-4 text-emerald-600" /> Provvigioni</p>
                {adminView ? (
                  <div className="rounded-xl border border-gray-200 overflow-hidden text-sm">
                    <div className="flex justify-between px-4 py-2.5 bg-gray-50/70"><span className="text-gray-500">Prezzo al pubblico</span><span className="font-medium text-gray-900">{formatPrezzo(b.prezzoPubblico)}</span></div>
                    <div className="flex justify-between px-4 py-2.5 border-t border-gray-100"><span className="text-gray-500">Costo negozio (IVA incl.)</span><span className="font-medium text-gray-900">{formatPrezzo(pi.prezzoNegozioIva)}</span></div>
                    <div className="flex justify-between px-4 py-2.5 border-t border-gray-100"><span className="text-gray-500">Margine</span><span className="font-medium text-gray-900">{formatPrezzo(pi.margine)}</span></div>
                    <div className="flex justify-between px-4 py-2.5 border-t border-gray-100"><span className="text-gray-500">Percentuale agente</span><span className="font-medium text-gray-900">{pi.percentuale}%</span></div>
                    <div className="flex justify-between px-4 py-2.5 border-t border-gray-100 bg-emerald-50/60"><span className="font-semibold text-emerald-800">Provvigione agente</span><span className="font-bold text-emerald-700">{formatPrezzo(pi.provvigioneAgente)}</span></div>
                    <div className="flex justify-between px-4 py-2.5 border-t border-gray-100"><span className="text-gray-500">Residuo azienda</span><span className="font-medium text-gray-900">{formatPrezzo(pi.provvigioneAzienda)}</span></div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-4 py-4 flex items-center justify-between">
                    <span className="text-sm text-emerald-800 font-medium">La tua provvigione su questo prodotto</span>
                    <span className="text-2xl font-bold text-emerald-700">{formatPrezzo(pi.provvigioneAgente)}</span>
                  </div>
                )}
                </div>}
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 bg-gray-50/50">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-colors">Chiudi</button>
            {b.schedaTecnicaPdf && (
              <button type="button" onClick={() => onOpenPdf(b.schedaTecnicaPdf)}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors">
                <DocumentTextIcon className="h-4 w-4" /> Scheda tecnica
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
